import React from "react";
import { Calendar, Clock, Users, MapPin, Video } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const GroupSessionCard = ({ session }) => {
  const availableSlots = session.maxParticipants - session.participants.length;
  const isFull = availableSlots === 0;
  const isAlmostFull = availableSlots <= 3 && availableSlots > 0;

  const categoryColors = {
    "stress-management": "blue",
    "anxiety-depression": "purple",
    "grief-loss": "gray",
    "substance-abuse": "orange",
    "relationship-issues": "pink",
    "trauma-recovery": "red",
    "self-esteem": "green",
    "anger-management": "yellow",
    "academic-challenges": "indigo",
    "social-skills": "teal",
    mindfulness: "cyan",
    other: "slate",
  };

  const color = categoryColors[session.category] || "blue";

  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="bg-white rounded-2xl border-2 border-gray-200 hover:border-blue-300 p-6 transition-all       
  hover:shadow-xl"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div
            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-2 bg-${color}-100     
  text-${color}-700`}
          >
            {session.category.replace("-", " ").toUpperCase()}
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {session.title}
          </h3>
          <p className="text-gray-600 text-sm line-clamp-2">
            {session.description}
          </p>
        </div>
      </div>

      {/* Session Details */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4 text-uzima-maroon-500" />
          <span>
            {new Date(session.sessionDate).toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock className="w-4 h-4 text-uzima-maroon-500" />
          <span>
            {session.startTime} - {session.endTime}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          {session.sessionType === "virtual" ? (
            <>
              <Video className="w-4 h-4 text-uzima-maroon-500" />
              <span>Virtual Session</span>
            </>
          ) : (
            <>
              <MapPin className="w-4 h-4 text-uzima-maroon-500" />
              <span>{session.location || "Uzima Counseling Center"}</span>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users className="w-4 h-4 text-uzima-maroon-500" />
          <span>
            {session.participants.length} / {session.maxParticipants}{" "}
            participants
            {!isFull && (
              <span
                className={`ml-2 ${
                  isAlmostFull ? "text-orange-600" : "text-green-600"
                } 
  font-semibold`}
              >
                ({availableSlots} spots left)
              </span>
            )}
          </span>
        </div>
      </div>

      {/* Facilitator */}
      <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
        <div
          className="w-10 h-10 rounded-full bg-gradient-to-r from-uzima-maroon-600 to-uzima-maroon-700 flex 
  items-center justify-center text-white font-bold"
        >
          {session.facilitator?.fullName?.charAt(0) || "C"}
        </div>
        <div>
          <p className="text-xs text-gray-600">Facilitated by</p>
          <p className="text-sm font-semibold text-gray-900">
            {session.facilitator?.fullName || "Counselor"}
          </p>
        </div>
      </div>

      {/* Action Button */}
      <Link
        to={`/group-sessions/${session._id}`}
        className={`block w-full text-center py-3 rounded-xl font-semibold transition ${
          isFull
            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
            : "bg-gradient-to-r from-uzima-maroon-600 to-uzima-maroon-700 text-white hover:shadow-lg"
        }`}
      >
        {isFull ? "Session Full" : "View Details & Register"}
      </Link>
    </motion.div>
  );
};

export default GroupSessionCard;
